import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Divider, ListItemButton, Typography, useColorScheme, useTheme } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import TranslateIcon from '@mui/icons-material/Translate';
import { useTranslation } from 'react-i18next';

interface RightDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const RightDrawer = (props: RightDrawerProps) => {
  const theme = useTheme();
  const { mode, setMode, systemMode } = useColorScheme();
  const { t, i18n } = useTranslation();
  if (!mode) {
    return null;
  }
  const resolvedMode = mode === 'system' ? systemMode : mode;

  return (
    <Drawer
      anchor="right"
      open={props.open}
      onClose={props.onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'transparent',
          },
        },
        paper: {
          sx: [
            {
              width: 280,
              boxSizing: 'border-box',
              borderTopLeftRadius: theme.borderRadii.br12,
              borderLeft: '1px solid',
              borderColor: theme.vars?.palette.divider,
              backgroundColor: 'transparent',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(20px)',
                pointerEvents: 'none',
                zIndex: -1,
                borderRadius: 'inherit',
              },
              [theme.breakpoints.down('md')]: {
                mt: '55px',
                width: '100%',
              },
            },
            (theme) =>
              theme.applyStyles('dark', {
                borderWidth: '0.5px',
                '&::before': {
                  backgroundColor: theme.vars?.palette.shades.neutral,
                },
              }),
          ],
        },
      }}
      sx={{
        width: 240,
        flexShrink: 0,
        zIndex: 1300,
      }}
      elevation={resolvedMode === 'dark' ? 0 : 5}
    >
      <Typography variant="h6" sx={{ ml: 3, mt: 2.5, mb: 0.9, fontSize: theme.fontSizes.f18 }}>
        {t('settings.title', 'Settings')}
      </Typography>
      <Divider
        sx={(theme) => ({
          ...theme.applyStyles('dark', {
            borderColor: theme.vars?.palette.grey[900],
            filter: 'blur(1px)',
          }),
        })}
      />
      <Box
        sx={{
          width: 'auto',
          height: '100%',
          mt: 6,
          ml: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          [theme.breakpoints.down('md')]: {
            height: 'calc(100vh - 170px)',
          },
        }}
        role="presentation"
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton
              disableRipple
              onClick={() => {
                setMode(resolvedMode === 'dark' ? 'light' : 'dark');
              }}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon>
                {resolvedMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </ListItemIcon>
              <ListItemText
                primary={
                  resolvedMode === 'dark'
                    ? t('settings.lightMode', 'Light Mode')
                    : t('settings.darkMode', 'Dark Mode')
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              disableRipple
              onClick={() => {
                void i18n.changeLanguage(i18n.language === 'nb' ? 'en' : 'nb');
              }}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon>
                <TranslateIcon />
              </ListItemIcon>
              <ListItemText primary={i18n.language === 'nb' ? 'English' : 'Norsk'} />
            </ListItemButton>
          </ListItem>
        </List>
        <List>
          <Divider />
          <ListItem
            disablePadding
            sx={{
              [theme.breakpoints.down('md')]: {
                mt: 3,
              },
            }}
          >
            <ListItemButton
              component="a"
              href="/.auth/logout?post_logout_redirect_uri=/.auth/login/aad"
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t('settings.logout', 'Logout')} />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};
